import { atom, atomStore, type PrimitiveAtom, selectAtom } from '@figma/jotai'
import { type Dispatch, type ThunkCreator, thunkCreator } from '@web/lib/action_helpers'
import { useAtomValue } from '@web/lib/jotai'
import { useMemo } from 'react'

export type LoadingState = {
  [key: string]: LoadingStatus
}

export type LoadingStateFailureReasons = {
  [key: string]: string | null
}

export enum LoadingStatus {
  INIT = 'INIT',
  LOADING = 'LOADING',
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE',
}

/** A very simple key-value store from a string to a LoadingStatus. */
export const loadingStateAtom: PrimitiveAtom<LoadingState> = atom<LoadingState>({})

/** The reason for a failure, associated with the same key as its LoadingState. */
export const loadingStateFailureReasonsAtom: PrimitiveAtom<LoadingStateFailureReasons> =
  atom<LoadingStateFailureReasons>({})

function setStatus(key: string, status: LoadingStatus) {
  atomStore.set(loadingStateAtom, (state) => ({ ...state, [key]: status }))
}

function deleteFailureReason(key: string) {
  atomStore.set(loadingStateFailureReasonsAtom, (state) => {
    const newState = { ...state }
    delete newState[key]
    return newState
  })
}

export const deleteLoadingState: ThunkCreator<{ key: string }> = /*#__PURE__*/ thunkCreator<{
  key: string
}>((_store, { key }) => {
  atomStore.set(loadingStateAtom, (state) => {
    const newState = { ...state }
    delete newState[key]
    return newState
  })
  deleteFailureReason(key)
})

export const putLoadingStateFailureReason: ThunkCreator<{ key: string; reason: string | null }> =
  /*#__PURE__*/ thunkCreator<{
    key: string
    reason: string | null
  }>((_store, { key, reason }) => {
    atomStore.set(loadingStateFailureReasonsAtom, (state) => ({ ...state, [key]: reason }))
  })

export const putLoadingStateFailure: ThunkCreator<{ key: string }> = /*#__PURE__*/ thunkCreator<{
  key: string
}>((_store, { key }) => {
  setStatus(key, LoadingStatus.FAILURE)
})

export const putLoadingStateSuccess: ThunkCreator<{ key: string }> = /*#__PURE__*/ thunkCreator<{
  key: string
}>((_store, { key }) => {
  setStatus(key, LoadingStatus.SUCCESS)
  deleteFailureReason(key)
})

export const putLoadingStateLoading: ThunkCreator<{ key: string }> = /*#__PURE__*/ thunkCreator<{
  key: string
}>((_store, { key }) => {
  setStatus(key, LoadingStatus.LOADING)
})

export function isLoading(loadingState: LoadingState, key: string): boolean {
  return loadingState[key] === LoadingStatus.LOADING
}

export function wasRequested(loadingState: LoadingState, key: string): boolean {
  return loadingState[key] !== null && loadingState[key] !== undefined
}

export function hasResolved(loadingState: LoadingState, key: string): boolean {
  return (
    loadingState[key] !== null && loadingState[key] !== undefined && !isLoading(loadingState, key)
  )
}

export function didSucceed(loadingState: LoadingState, key: string): boolean {
  return loadingState[key] === LoadingStatus.SUCCESS
}

export function didFail(loadingState: LoadingState, key: string): boolean {
  return loadingState[key] === LoadingStatus.FAILURE
}

export function shouldRetry(loadingState: LoadingState, key: string): boolean {
  return (
    loadingState[key] === null ||
    loadingState[key] === undefined ||
    loadingState[key] === LoadingStatus.FAILURE
  )
}

/** The `loadingState` map, for components to pass to the helpers above. */
export function useLoadingState(): LoadingState {
  return useAtomValue(loadingStateAtom, { deferToFrame: true })
}

/**
 * The status recorded for `key`, or `undefined` when the request was never made.
 *
 * A nullish `key` returns `undefined`. This supports use of this hook in components that can only
 * conditionally build a key.
 */
export function useLoadingStatus(key: string | null | undefined): LoadingStatus | undefined {
  // Narrow to the single key so an unrelated key's status changing does not re-render.
  // https://jotai.org/docs/utilities/select#hold-stable-references
  const statusAtom = useMemo(
    () =>
      selectAtom(loadingStateAtom, (state) =>
        key === null || key === undefined ? undefined : state[key],
      ),
    [key],
  )
  return useAtomValue(statusAtom, { deferToFrame: true })
}

/** The reason recorded for `key`'s failure. */
export function useFailureReason(key: string): string | null | undefined {
  // Narrow to the single key so an unrelated key's reason changing does not re-render.
  // https://jotai.org/docs/utilities/select#hold-stable-references
  const reasonAtom = useMemo(
    () => selectAtom(loadingStateFailureReasonsAtom, (state) => state[key]),
    [key],
  )
  return useAtomValue(reasonAtom, { deferToFrame: true })
}

export function useIsLoading(key: string): boolean {
  const loadingState = useLoadingState()
  return isLoading(loadingState, key)
}

export function useWasRequested(key: string): boolean {
  const loadingState = useLoadingState()
  return wasRequested(loadingState, key)
}

export function useHasResolved(key: string): boolean {
  const loadingState = useLoadingState()
  return hasResolved(loadingState, key)
}

export function useDidSucceed(key: string): boolean {
  const loadingState = useLoadingState()
  return didSucceed(loadingState, key)
}

export function useDidFail(key: string): boolean {
  const loadingState = useLoadingState()
  return didFail(loadingState, key)
}

/**
 * Take in a Promise and automatically update the loading state of a key
 * before and after the Promise resolves.
 * If the optional extractFailureReason callback is provided, it will be called
 * on the error object if the Promise rejects, and the result will be stored in
 * `loadingStateFailureReasonsAtom` under the same key.
 *
 * Note that this function (and loading state in general) is only useful for async
 * operations. Any expensive synchronous operations (e.g. making a call to
 * fullscreen to generate thumbnails) won't be able to respond to updates as
 * actions get dispatched. For these cases, you should probably show a spinner
 * via CSS, which can be rendered by the browser as the main thread is locked up.
 */
export function registerLoadingState(
  req: Promise<unknown>,
  store: { dispatch: Dispatch },
  key: string,
  extractFailureReason?: (err: unknown) => string | undefined,
): void {
  store.dispatch(putLoadingStateLoading({ key }))
  req
    .then(() => {
      store.dispatch(putLoadingStateSuccess({ key }))
    })
    .catch((err) => {
      if (extractFailureReason) {
        store.dispatch(
          putLoadingStateFailureReason({ key, reason: extractFailureReason(err) || null }),
        )
      }
      store.dispatch(putLoadingStateFailure({ key }))
    })
}
